import { useEffect } from 'react'

import useAuth from 'hooks/use-auth'
import { useGremlinPagination } from 'hooks/use-gremlin-query'
import { agentsQueryKeys } from 'queries/agents/query-keys'
import {
  API_CALL_THROTTLE_TIME_MS,
  getEndpointWithParams,
  PAGE_SIZE_DEFAULT,
  type PaginatedQueryParams
} from 'queries/queries-utils'
import { ms } from 'utils/time/millisecond'

import type { AxiosError } from 'axios'
import type { UseInfiniteQueryOptions } from 'react-query/types/react/types'
import type { PagedAgentsResponse } from 'types/api/agents/agents'

export const useGetAgentsPaged = (
  queryParams?: PaginatedQueryParams,
  options?: Omit<
    UseInfiniteQueryOptions<PagedAgentsResponse, AxiosError>,
    'queryKey'
  >
) => {
  const { currentTeamId: teamId } = useAuth()

  const { pageSize = PAGE_SIZE_DEFAULT, ...queryParamsRest } = queryParams ?? {}

  const queryKey = agentsQueryKeys.get({
    pageSize,
    teamId,
    ...queryParamsRest
  })

  const [endpoint, params] = queryKey

  const endpointWithParams = getEndpointWithParams(endpoint, params)

  const agentsPagedQueryResponse = useGremlinPagination(
    endpointWithParams,
    {},
    {
      staleTime: ms.minutes(5), // allow this to be overwritten
      ...options,
      queryKey
    },
    teamId
  )

  const { isFetchingNextPage, fetchNextPage, hasNextPage } =
    agentsPagedQueryResponse

  // fetch remaining pages (throttled) until complete
  useEffect(() => {
    let timeout: NodeJS.Timeout
    if (hasNextPage && !isFetchingNextPage) {
      timeout = setTimeout(() => {
        fetchNextPage()
      }, API_CALL_THROTTLE_TIME_MS)
    }
    return () => clearTimeout(timeout)
  }, [fetchNextPage, hasNextPage, isFetchingNextPage])

  return agentsPagedQueryResponse
}
